import { Grid } from '@material-ui/core';
import { useField, useFormikContext } from 'formik';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../../Session';
import { useGetUserListByTypeQuery, UserType, SettingValueUnit } from '../../../api';
import OutlinedDatePickerField from '../../../components/fields/OutlinedDatePickerField';
import OutlinedNumericUnitInputField from '../../../components/fields/OutlinedNumericUnitInputField';
import OutlinedSelectField from '../../../components/fields/OutlinedSelectField';
import useCommonStyles from '../../../useCommonStyles';
import useBiddingSetting from '../../../utilities/useBiddingSetting';
import useSalesTransactionActionOptions from '../../../utilities/useSalesTransactionActionOptions';
import useSettingValueUnitDefaults from '../../../utilities/useSettingValueUnitDefaults';

const TradeInDetailsForm = ({ isEditable = false }: { isEditable?: boolean }) => {
    const { t } = useTranslation(['common', 'saleTransactionsPage']);
    const commonStyles = useCommonStyles();
    const salesTransactionActionOptions = useSalesTransactionActionOptions();
    const user = useCurrentUser();

    const { setFieldValue, values } = useFormikContext<any>();
    const [{ value: handlingChargesUnit }] = useField<SettingValueUnit | ''>({ name: 'handlingCharges.unit' });
    const [{ value: marginUnit }] = useField<SettingValueUnit | ''>({ name: 'margin.unit' });
    const handlingChargesValue = values?.handlingCharges?.value;
    const marginValue = values?.margin?.value;
    const intendedDeregistrationDate = values?.vehicle?.intendedDeregistrationDate;
    const targetHandoverDateTime = values?.handover?.targetHandoverDateTime;
    const hasInitializedTargetHandoverDateTime = useRef(false);

    const { biddingSetting } = useBiddingSetting('cache-first');
    const { handlingFeesSetting, marginSetting } = useMemo(() => {
        if (biddingSetting) {
            return {
                handlingFeesSetting: biddingSetting.handlingFees,
                marginSetting: biddingSetting.margin,
            };
        }

        return { handlingFeesSetting: undefined, marginSetting: undefined };
    }, [biddingSetting]);

    const { ensureUnitAndMaybeValue, onUnitChanged } = useSettingValueUnitDefaults(setFieldValue);

    const disableHandlingChargesAndMargin = useMemo(
        () => ![UserType.Admin, UserType.SalesManager, UserType.Purchaser].includes(user.type),
        [user.type]
    );

    useEffect(() => {
        ensureUnitAndMaybeValue('handlingCharges', handlingFeesSetting, handlingChargesUnit, handlingChargesValue);
        ensureUnitAndMaybeValue('margin', marginSetting, marginUnit, marginValue);
    }, [
        ensureUnitAndMaybeValue,
        handlingChargesUnit,
        handlingChargesValue,
        handlingFeesSetting,
        marginSetting,
        marginUnit,
        marginValue,
    ]);

    useEffect(() => {
        if (hasInitializedTargetHandoverDateTime.current) {
            return;
        }

        if (targetHandoverDateTime) {
            hasInitializedTargetHandoverDateTime.current = true;

            return;
        }

        if (intendedDeregistrationDate) {
            setFieldValue('handover.targetHandoverDateTime', intendedDeregistrationDate);
            hasInitializedTargetHandoverDateTime.current = true;
        }
    }, [intendedDeregistrationDate, setFieldValue, targetHandoverDateTime]);

    const { data } = useGetUserListByTypeQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            type: UserType.SaleConsultant,
        },
    });

    const salesConsultantOptions = useMemo(
        () =>
            (data?.users?.items || []).map(user => ({
                value: user.id,
                label: user.fullName,
            })),
        [data]
    );

    return (
        <Grid spacing={2} container>
            <Grid md={6} sm={12} xs={12} item>
                <OutlinedDatePickerField
                    color="secondary"
                    label={t('saleTransactionsPage:handover.targetHandoverDate')}
                    name="handover.targetHandoverDateTime"
                    placeholder={t('saleTransactionsPage:handover.targetHandoverDate')}
                    required
                />
            </Grid>
            {!isEditable && (
                <Grid md={6} sm={12} xs={12} item>
                    <OutlinedSelectField
                        color="secondary"
                        inputProps={{
                            classes: {
                                icon: commonStyles.fillSecondary,
                            },
                        }}
                        label={t('saleTransactionsPage:tradeInDetails.action')}
                        name="action"
                        options={salesTransactionActionOptions}
                        placeholder={t('saleTransactionsPage:tradeInDetails.action')}
                        required
                    />
                </Grid>
            )}
            <Grid md={6} sm={12} xs={12} item>
                <OutlinedNumericUnitInputField
                    color="secondary"
                    disabled={disableHandlingChargesAndMargin}
                    formatToFixed={2}
                    inputProps={{
                        classes: {
                            icon: commonStyles.fillSecondary,
                        },
                        inputMode: 'decimal',
                    }}
                    label={t('saleTransactionsPage:tradeInDetails.handlingCharges')}
                    minMax={{ percentage: { min: 0, max: 100 }, amount: { min: 0 } }}
                    name="handlingCharges"
                    onUnitChanged={unit =>
                        onUnitChanged('handlingCharges', handlingFeesSetting, unit as SettingValueUnit)
                    }
                    placeholder={t('saleTransactionsPage:tradeInDetails.handlingCharges')}
                    type="number"
                    required
                />
            </Grid>
            <Grid md={6} sm={12} xs={12} item>
                <OutlinedNumericUnitInputField
                    color="secondary"
                    disabled={disableHandlingChargesAndMargin}
                    formatToFixed={2}
                    inputProps={{
                        classes: {
                            icon: commonStyles.fillSecondary,
                        },
                        inputMode: 'decimal',
                    }}
                    label={t('saleTransactionsPage:tradeInDetails.margin')}
                    minMax={{ percentage: { min: 0, max: 100 }, amount: { min: 0 } }}
                    name="margin"
                    onUnitChanged={unit => onUnitChanged('margin', marginSetting, unit as SettingValueUnit)}
                    placeholder={t('saleTransactionsPage:tradeInDetails.margin')}
                    type="number"
                    required
                />
            </Grid>
            <Grid md={6} sm={12} xs={12} item>
                <OutlinedSelectField
                    color="secondary"
                    inputProps={{
                        classes: {
                            icon: commonStyles.fillSecondary,
                        },
                    }}
                    label={t('saleTransactionsPage:tradeInDetails.salesConsultant')}
                    name="salesConsultant"
                    options={salesConsultantOptions}
                    placeholder={t('saleTransactionsPage:tradeInDetails.salesConsultant')}
                    required
                />
            </Grid>
        </Grid>
    );
};

export default TradeInDetailsForm;
